import { useRef, useState } from "react";
import {
  AlertCircle,
  ArrowUpFromLine,
  CheckCircle2,
  Clock3,
  Files,
  Inbox,
  LoaderCircle,
  UploadCloud,
} from "lucide-react";
import {
  AppPageShell,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { MailFilingDialog } from "./MailFilingDialog";
import { useUser } from "@/components/UserProvider";
import {
  isMailIntakeActive,
  type MailIntakeItem,
} from "@/lib/data/schemas/mail-intake";
import { fileOperationMessage } from "@/lib/data/requests/files/canonical-files";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { MailIntakeRow } from "./MailIntakeRow";
import { useMailIntake } from "./useMailIntake";

export function MailSortingPage() {
  const { hasAnyPagePermission } = useUser();
  const canRead = hasAnyPagePermission([
    PAGE_PERMISSION_KEYS.filesAccess,
    PAGE_PERMISSION_KEYS.firmFiles,
  ]);
  return canRead ? (
    <MailSortingContent />
  ) : (
    <AppPageShell>
      <p className="text-muted-foreground p-6">
        You need access to Firm Files to use mail sorting.
      </p>
    </AppPageShell>
  );
}

function MailSortingContent() {
  const mail = useMailIntake();
  const { hasGrant } = useUser();
  const canUpload = hasGrant("firmFile", "create");
  const [dragging, setDragging] = useState(false);
  const [filter, setFilter] = useState("all");
  const [moveItem, setMoveItem] = useState<MailIntakeItem>();
  const input = useRef<HTMLInputElement>(null);
  const active = mail.items.filter(isMailIntakeActive).length;
  const attention = mail.items.filter((item) =>
    ["needs_attention", "failed"].includes(item.status),
  ).length;
  const filed = mail.items.filter((item) => item.status === "filed").length;
  const visible = mail.items.filter(
    (item) =>
      filter === "all" ||
      (filter === "active"
        ? isMailIntakeActive(item)
        : filter === "attention"
          ? ["needs_attention", "failed"].includes(item.status)
          : item.status === "filed"),
  );
  const savedIds = new Set(
    mail.localUploads
      .filter((upload) => upload.error)
      .map((upload) => upload.reservedId),
  );
  const choose = () => input.current?.click();
  const onFiles = (files: File[]) => {
    if (canUpload && !mail.uploading) {
      setFilter("all");
      void mail.upload(files);
    }
  };
  return (
    <AppPageShell className="mx-auto max-w-[1500px] gap-6 lg:p-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Mail Sorting</h1>
      </header>
      <input
        ref={input}
        type="file"
        multiple
        className="sr-only"
        aria-label="Select files for mail sorting"
        disabled={!canUpload || mail.uploading}
        onChange={(event) => {
          onFiles(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />
      <div
        onDragOver={(event) => {
          event.preventDefault();
          if (canUpload && !mail.uploading) setDragging(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node))
            setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          onFiles(Array.from(event.dataTransfer.files));
        }}
        className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-9 text-center transition-colors ${dragging ? "border-primary bg-primary/10" : "border-border bg-muted/20"}`}
      >
        <div className="bg-background mb-4 flex size-12 items-center justify-center rounded-xl border shadow-sm">
          <UploadCloud className="text-primary size-6" />
        </div>
        <h2 className="text-base font-medium">
          {mail.uploading
            ? "Uploading your documents…"
            : "Drop incoming files here"}
        </h2>
        <p className="text-muted-foreground mt-2 max-w-lg text-sm">
          Upload up to 100 files. We’ll find their matter and file them in an
          existing folder, or ask you to choose one.
        </p>
        <Button
          className="mt-5"
          onClick={choose}
          disabled={!canUpload || mail.uploading}
        >
          {mail.uploading ? (
            <LoaderCircle className="mr-2 size-4 animate-spin" />
          ) : (
            <ArrowUpFromLine className="mr-2 size-4" />
          )}
          {mail.uploading ? "Uploading" : "Choose files"}
        </Button>
        {!canUpload ? (
          <p className="mt-2 text-xs text-amber-700">
            You need permission to create Firm Files to upload.
          </p>
        ) : null}
      </div>
      {mail.uploadError ? (
        <p role="alert" className="text-destructive text-sm">
          {mail.uploadError}
        </p>
      ) : null}
      <div className="grid grid-cols-3 gap-3">
        {[
          {
            label: "In progress",
            count:
              active + mail.localUploads.filter((item) => !item.error).length,
            icon: Clock3,
          },
          { label: "Filed", count: filed, icon: CheckCircle2 },
          {
            label: "Needs attention",
            count:
              attention + mail.localUploads.filter((item) => item.error).length,
            icon: AlertCircle,
          },
        ].map(({ label, count, icon: Icon }) => (
          <div
            key={label}
            className="bg-card flex items-center gap-3 rounded-xl border px-4 py-4"
          >
            <Icon className="text-muted-foreground hidden size-5 sm:block" />
            <div>
              <p className="text-xl font-semibold tabular-nums">{count}</p>
              <p className="text-muted-foreground text-xs">{label}</p>
            </div>
          </div>
        ))}
      </div>
      <section className="bg-card overflow-hidden rounded-xl border">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
          <div className="flex items-center gap-2">
            <Files className="text-muted-foreground size-4" />
            <h2 className="text-sm font-semibold">
              {mail.batchId ? "This upload" : "Recent uploads"}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger
                aria-label="Filter files"
                className="h-8 w-[170px] text-xs"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[
                  ["all", "All files"],
                  ["active", "In progress"],
                  ["filed", "Filed"],
                  ["attention", "Needs attention"],
                ].map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {mail.batchId ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  mail.setBatchId();
                  setFilter("all");
                }}
              >
                View recent uploads
              </Button>
            ) : null}
          </div>
        </div>
        {mail.query.isError ? (
          <div role="alert" className="p-5 text-sm">
            <p className="text-destructive">
              {fileOperationMessage(
                mail.query.error,
                "Progress could not be loaded. Your uploaded files continue processing.",
              )}
            </p>
          </div>
        ) : null}
        {mail.localUploads.map((upload) => (
          <div
            key={upload.key}
            className="flex items-start gap-3 border-b px-5 py-4"
          >
            <FileTextIcon />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{upload.name}</p>
              <p
                className={`mt-1 text-xs ${upload.error ? "text-destructive" : "text-muted-foreground"}`}
              >
                {upload.error ?? "Preparing upload…"}
              </p>
            </div>
            {!upload.error ? (
              <LoaderCircle className="text-muted-foreground ml-auto size-4 animate-spin" />
            ) : null}
          </div>
        ))}
        {visible
          .filter((item) => !savedIds.has(item.id))
          .map((item) => (
            <MailIntakeRow
              key={item.id}
              item={item}
              onChooseFolder={() => {
                setMoveItem(item);
              }}
            />
          ))}
        {!visible.length && !mail.localUploads.length && !mail.query.isError ? (
          <div className="text-muted-foreground flex flex-col items-center px-5 py-12 text-center">
            <Inbox className="mb-3 size-8 opacity-50" />
            <p className="text-sm">
              {mail.query.isPending
                ? "Loading uploads…"
                : mail.items.length
                  ? "No files in this view."
                  : "Your uploads will appear here."}
            </p>
            <p className="mt-1 text-xs">
              Progress is saved, so you can leave this page and come back.
            </p>
          </div>
        ) : null}
        {mail.query.hasNextPage ? (
          <div className="border-t p-3 text-center">
            <Button
              size="sm"
              variant="ghost"
              disabled={mail.query.isFetchingNextPage}
              onClick={() => void mail.query.fetchNextPage()}
            >
              Load more
            </Button>
          </div>
        ) : null}
      </section>
      {moveItem?.fileId && moveItem.revision ? (
        <MailFilingDialog
          item={moveItem}
          onClose={() => {
            setMoveItem(undefined);
            void mail.refresh();
          }}
        />
      ) : null}
    </AppPageShell>
  );
}

function FileTextIcon() {
  return (
    <div className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-lg">
      <Files className="size-4" />
    </div>
  );
}
